import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import BackToTop from './components/BackToTop';
import PageLoader from './components/PageLoader';
import { MenuProvider, useMenu } from './context/MenuContext';
import SideMenu from './components/SideMenu';
import FloatingPageCard from './components/FloatingPageCard';
import { useAnalytics } from './hooks/useAnalytics';
import './styles/index.css';

const Home = React.lazy(() => import('./pages/Home'));
const About = React.lazy(() => import('./pages/About'));
const Contact = React.lazy(() => import('./pages/Contact'));
const AISolutions = React.lazy(() => import('./pages/AISolutions'));
const Departments = React.lazy(() => import('./pages/Departments'));
const Audit = React.lazy(() => import('./pages/Audit'));
const Integrations = React.lazy(() => import('./pages/Integrations'));
const Templates = React.lazy(() => import('./pages/Templates'));
const Privacy = React.lazy(() => import('./pages/Privacy'));
const Terms = React.lazy(() => import('./pages/Terms'));
const LocalSetups = React.lazy(() => import('./pages/LocalSetups'));
const Keanu = React.lazy(() => import('./pages/team/Keanu'));
const Justin = React.lazy(() => import('./pages/team/Justin'));
const NotFound = React.lazy(() => import('./pages/NotFound'));

const AppContent: React.FC = () => {
  const { isMenuOpen, closeMenu } = useMenu();
  const location = useLocation();

  useAnalytics();

  React.useEffect(() => {
    closeMenu();
    // eslint-disable-next-line
  }, [location.pathname]);

  React.useEffect(() => {
    const prefetch = () => {
      void import('./pages/Audit');
      void import('./pages/AISolutions');
      void import('./pages/Departments');
      void import('./pages/Contact');
    };

    const idleWindow = window as Window & {
      requestIdleCallback?: (cb: () => void) => number;
      cancelIdleCallback?: (id: number) => void;
    };

    if (idleWindow.requestIdleCallback) {
      const id = idleWindow.requestIdleCallback(prefetch);
      return () => idleWindow.cancelIdleCallback?.(id);
    }

    const timeoutId = window.setTimeout(prefetch, 1200);
    return () => window.clearTimeout(timeoutId);
  }, []);

  return (
    <div className={`App${isMenuOpen ? ' menu-open' : ''}`}>
      <div id="modal-portal"></div>
      {isMenuOpen && (
        <div className="menu-overlay">
          <SideMenu />
        </div>
      )}

      <FloatingPageCard isMenuOpen={isMenuOpen}>
        {!isMenuOpen && <Header />}
        <main className="main-content">
          <React.Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/ai-solutions" element={<AISolutions />} />
              <Route path="/departments" element={<Departments />} />
              <Route path="/security" element={<Navigate to="/departments#security" replace />} />
              <Route path="/audit" element={<Audit />} />
              <Route path="/integrations" element={<Integrations />} />
              <Route path="/templates" element={<Templates />} />
              <Route path="/team/keanu" element={<Keanu />} />
              <Route path="/team/justin" element={<Justin />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/local-setups" element={<LocalSetups />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </React.Suspense>
        </main>
        <Footer />
      </FloatingPageCard>

      <ScrollToTop />
      <BackToTop />
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <Router>
        <MenuProvider>
          <AppContent />
        </MenuProvider>
      </Router>
    </HelmetProvider>
  );
}

export default App;
